import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, ImageUploader, Button, SelectField, UserAvatar, Confirm, ProjectSelectField, LangField, LabelWithHelp, FieldHelp, MultiSelectField, FormActions, DangerZone } from '../../../components';
import type { ImageListType, IconType } from '../../../components';
import { User } from '../../../models';
import { RootState, useAppSelector, useAppDispatch, setUserAvatar, unsetUserAvatar, deactivateUser, activateUser } from '../../../redux';
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import type { FormikValues } from 'formik';
import { Users } from '../../../services';
import CalendarSubscriptionPanel from './CalendarSubscriptionPanel';
import { ToggleSwitch } from 'flowbite-react';
import {
    getDesktopNotificationPermission,
    isDesktopNotificationSupported,
    requestDesktopNotificationPermission,
    type DesktopPermission,
} from '../../../utils/desktopNotifications';
import { applyThemeClass, normalizeTheme, storeTheme, THEME_OPTIONS, type ThemePreference } from '../../../utils/theme';
import {
    applyFontFamily,
    FONT_PACKS,
    loadFontCatalog,
    normalizeFontFamily,
    storeFontFamily,
    type FontFamilyId,
} from '../../../utils/fonts';
import {
    normalizeUiLanguage,
    UI_HOME_PAGES,
    UI_LANGUAGES,
} from '../../../utils/uiPreferences';
import i18n from '../../../locales/i18n';
/** Form values shape for Formik context (avoids `values` typed as unknown). */
type UserFormValues = FormikValues & {
    account_type?: string;
    jwt_expires_in?: string;
    notificationPreferences?: {
        channels?: string[];
        language?: string;
    };
    appearancePreferences?: {
        theme?: string;
        fontFamily?: string;
    };
    trackingPreferences?: {
        defaultProjectId?: string | null;
        defaultBillingMode?: string;
    };
    uiPreferences?: {
        language?: string;
        homePage?: string;
    };
};

type UserFormTab = [string, string];

interface UserFormProps {
    isCreation?: boolean,
    isLoading?: boolean,
    initialValues: FormikValues,
    onSubmit: (values: AnyObj) => void
}

function buildUserFormTabs(
    isCreation: boolean | undefined,
    isBot: boolean,
    hasPatTab: boolean,
    hasCalendarTab: boolean,
    canEditProjects: boolean
): UserFormTab[] {
    if (isCreation && isBot)
        return [['general', 'info']];
    if (isCreation && !isBot)
        return [['general', 'info'], ['authentication', 'lock']];

    const tabs: UserFormTab[] = [['general', 'info'], ['preferences', 'settings']];
    if (!isBot)
        tabs.push(['authentication', 'lock']);
    if (canEditProjects)
        tabs.push(['projects', 'project']);
    if (hasPatTab)
        tabs.push(['mcp_tokens', 'lock']);
    if (hasCalendarTab)
        tabs.push(['calendar', 'date']);
    return tabs;
}

function tabIndexOf(tabs: UserFormTab[], label: string): number {
    return tabs.findIndex(([tabLabel]) => tabLabel === label);
}

function useUserFormTabs(
    isCreation: boolean | undefined,
    hasPatTab: boolean,
    hasCalendarTab: boolean,
    canEditProjects: boolean
): UserFormTab[] {
    const { values } = useFormikContext<UserFormValues>();
    const isBot = values.account_type === User.ACCOUNT_TYPE_BOT;
    return useMemo(
        () => buildUserFormTabs(isCreation, isBot, hasPatTab, hasCalendarTab, canEditProjects),
        [isCreation, isBot, hasPatTab, hasCalendarTab, canEditProjects]
    );
}

function UserFormTabsBridge({ isCreation, hasPatTab, hasCalendarTab, canEditProjects, children }: {
    isCreation?: boolean;
    hasPatTab: boolean;
    hasCalendarTab: boolean;
    canEditProjects: boolean;
    children: (ctx: {
        tabs: UserFormTab[];
        preferencesTabIndex: number;
        authTabIndex: number;
        projectsTabIndex: number;
        patTabIndex: number;
        calendarTabIndex: number;
    }) => React.ReactNode;
}) {
    const tabs = useUserFormTabs(isCreation, hasPatTab, hasCalendarTab, canEditProjects);
    return (
        <>
            {children({
                tabs,
                preferencesTabIndex: tabIndexOf(tabs, 'preferences'),
                authTabIndex: tabIndexOf(tabs, 'authentication'),
                projectsTabIndex: tabIndexOf(tabs, 'projects'),
                patTabIndex: tabIndexOf(tabs, 'mcp_tokens'),
                calendarTabIndex: tabIndexOf(tabs, 'calendar'),
            })}
        </>
    );
}

function UserFormTabReset({ activeTab, setActiveTab, isCreation }: {
    activeTab: number;
    setActiveTab: (i: number) => void;
    isCreation?: boolean;
}) {
    const { values } = useFormikContext<UserFormValues>();
    const isBot = values.account_type === User.ACCOUNT_TYPE_BOT;
    useEffect(() => {
        if (isCreation && isBot && activeTab > 0)
            setActiveTab(0);
    }, [isBot, isCreation, activeTab, setActiveTab]);
    return null;
}

function UserFormSaveBar({ isCreation, activeTab, authTabIndex, patTabIndex, calendarTabIndex, isLoading }: {
    isCreation?: boolean;
    activeTab: number;
    authTabIndex: number;
    patTabIndex: number;
    calendarTabIndex: number;
    isLoading?: boolean;
}) {
    const { values } = useFormikContext<UserFormValues>();
    const isBot = values.account_type === User.ACCOUNT_TYPE_BOT;
    if (patTabIndex >= 0 && activeTab === patTabIndex)
        return null;
    if (calendarTabIndex >= 0 && activeTab === calendarTabIndex)
        return null;
    if (isCreation) {
        if (isBot && activeTab !== 0)
            return null;
        if (!isBot && activeTab !== authTabIndex)
            return null;
    }
    return (
        <FormActions
            cancelTo="/users"
            loading={isLoading}
        />
    );
}

function UserFormTabSidebar({ tabs, activeTab, setActiveTab, t }: {
    tabs: UserFormTab[];
    activeTab: number;
    setActiveTab: (i: number) => void;
    t: (k: string, o?: AnyObj) => string;
}) {
    return (
        <ul className="space-y-0 font-medium text-gray-600 dark:text-gray-300 border-t dark:border-gray-700">
            {tabs.map(([label, icon], i: number) => (
                <li
                    key={label}
                    className={`cursor-pointer w-full text-sm py-3 ${
                        activeTab === i
                            ? "text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-700"
                            : "hover:bg-gray-100 dark:hover:bg-gray-700/60"
                    }`}
                    onClick={() => setActiveTab(i)}
                >
                    <p className="ms-3">
                        <Icon color={activeTab === i ? 'gray-900' : 'gray-500'} type={icon as IconType} className="inline mr-1" />
                        {t(`users.${label}`)}
                    </p>
                </li>
            ))}
        </ul>
    );
}

function PreferencesTabFields({
    preferencesTabIndex,
    activeTab,
    canPreviewTheme,
}: {
    preferencesTabIndex: number;
    activeTab: number;
    canPreviewTheme: boolean;
}) {
    const { t } = useTranslation();
    const { values, setFieldValue } = useFormikContext<UserFormValues>();

    useEffect(() => {
        if (preferencesTabIndex < 0 || activeTab !== preferencesTabIndex)
            return;
        loadFontCatalog();
    }, [activeTab, preferencesTabIndex]);

    if (preferencesTabIndex < 0 || activeTab !== preferencesTabIndex)
        return null;

    const theme = normalizeTheme(values.appearancePreferences?.theme);
    const fontFamily = normalizeFontFamily(values.appearancePreferences?.fontFamily);

    const onThemeChange = (next: ThemePreference) => {
        setFieldValue('appearancePreferences.theme', next);
        if (!canPreviewTheme)
            return;
        applyThemeClass(next);
        storeTheme(next);
    };

    const onFontChange = (next: FontFamilyId) => {
        setFieldValue('appearancePreferences.fontFamily', next);
        if (!canPreviewTheme)
            return;
        applyFontFamily(next);
        storeFontFamily(next);
    };

    const fontLabel = (pack: typeof FONT_PACKS[number]) => (
        pack.id === 'system' ? t('users.font_pack_system') : pack.name
    );

    return (
        <div className="flex-1 space-y-10 text-left">
            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('users.preferences_tracking')}</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.preferences_tracking_desc')}</p>
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div className="col-span-1">
                        <LabelWithHelp htmlFor="trackingPreferences.defaultProjectId" help={t('users.help.default_tracking_project') || ''}>
                            {t('users.default_tracking_project')}
                        </LabelWithHelp>
                        <div className="mt-2">
                            <ProjectSelectField
                                name="trackingPreferences.defaultProjectId"
                                placeholder={t('users.default_tracking_project_placeholder') || ''}
                            />
                        </div>
                    </div>
                    <div className="col-span-1">
                        <LabelWithHelp htmlFor="trackingPreferences.defaultBillingMode" help={t('users.help.default_billing_mode') || ''}>
                            {t('users.default_billing_mode')}
                        </LabelWithHelp>
                        <div className="mt-2">
                            <SelectField
                                name="trackingPreferences.defaultBillingMode"
                                placeholder={t('users.default_billing_mode_placeholder') || ''}
                                options={[
                                    { value: 'hourly', label: t('tracks.billing_mode_hourly') },
                                    { value: 'package', label: t('tracks.billing_mode_package') },
                                    { value: 'free', label: t('tracks.billing_mode_free') },
                                    { value: 'floor', label: t('tracks.billing_mode_floor') },
                                ]}
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('users.preferences_notifications')}</h3>
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div className="col-span-1">
                        <LabelWithHelp htmlFor="notificationPreferences.channels" help={t('users.help.notification_channels') || ''}>
                            {t('users.notification_channels')}
                        </LabelWithHelp>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.notification_channels_desc')}</p>
                        <div className="mt-2">
                            <MultiSelectField
                                name="notificationPreferences.channels"
                                placeholder={t('users.notification_channels_placeholder') || ''}
                                options={[
                                    { value: 'email', label: t('users.notification_channel_email') },
                                    { value: 'signal', label: t('users.notification_channel_signal') },
                                    { value: 'desktop', label: t('users.notification_channel_desktop') },
                                ]}
                            />
                            <ErrorMessage name="notificationPreferences.channels" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                            <DesktopNotificationPermissionField />
                        </div>
                    </div>
                    <div className="col-span-1">
                        <LabelWithHelp htmlFor="notificationPreferences.language" help={t('users.help.notification_language') || ''}>
                            {t('users.notification_language')}
                        </LabelWithHelp>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.notification_language_desc')}</p>
                        <div className="mt-2">
                            <LangField
                                name="notificationPreferences.language"
                                placeholder={t('users.notification_language_placeholder') || ''}
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('users.preferences_interface')}</h3>
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div className="col-span-1">
                        <LabelWithHelp htmlFor="uiPreferences.language" help={t('users.help.ui_language') || ''}>
                            {t('users.ui_language')}
                        </LabelWithHelp>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.ui_language_desc')}</p>
                        <div className="mt-2">
                            <SelectField
                                name="uiPreferences.language"
                                placeholder={t('users.ui_language_placeholder') || ''}
                                options={UI_LANGUAGES.map((lang) => ({
                                    value: lang,
                                    label: lang.toUpperCase(),
                                }))}
                            />
                        </div>
                    </div>
                    <div className="col-span-1">
                        <LabelWithHelp htmlFor="uiPreferences.homePage" help={t('users.help.home_page') || ''}>
                            {t('users.home_page')}
                        </LabelWithHelp>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.home_page_desc')}</p>
                        <div className="mt-2">
                            <SelectField
                                name="uiPreferences.homePage"
                                placeholder={t('users.home_page_placeholder') || ''}
                                options={UI_HOME_PAGES.map((page) => ({
                                    value: page,
                                    label: t(`users.home_page_${page}`),
                                }))}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <label htmlFor="appearancePreferences.theme" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('users.theme')}
                    </label>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.theme_desc')}</p>
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {THEME_OPTIONS.map((option) => {
                            const selected = theme === option;
                            return (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => onThemeChange(option)}
                                    className={`rounded-md border px-3 py-2 text-sm font-medium transition ${
                                        selected
                                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-900 dark:border-indigo-400 dark:bg-indigo-950 dark:text-indigo-100'
                                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    {t(`users.theme_${option}`)}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <label htmlFor="appearancePreferences.fontFamily" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('users.font_family')}
                    </label>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.font_family_desc')}</p>
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                        {FONT_PACKS.map((pack) => {
                            const selected = fontFamily === pack.id;
                            return (
                                <button
                                    key={pack.id}
                                    type="button"
                                    onClick={() => onFontChange(pack.id)}
                                    className={`rounded-md border px-3 py-2.5 text-left transition ${
                                        selected
                                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-900 dark:border-indigo-400 dark:bg-indigo-950 dark:text-indigo-100'
                                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    <span className="block text-base font-semibold leading-tight" style={{ fontFamily: pack.previewFamily }}>
                                        {fontLabel(pack)}
                                    </span>
                                    <span className="mt-0.5 block text-xs opacity-70" style={{ fontFamily: pack.previewFamily }}>
                                        Aa Bb 123
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}

function UserFormPatTab({ patTabIndex, activeTab, children }: {
    patTabIndex: number;
    activeTab: number;
    children: React.ReactNode;
}) {
    if (patTabIndex < 0 || activeTab !== patTabIndex)
        return null;
    return <>{children}</>;
}

function PasswordTabFields({ authTabIndex, activeTab, children }: {
    authTabIndex: number;
    activeTab: number;
    children: React.ReactNode;
}) {
    const { values } = useFormikContext<UserFormValues>();
    if (values.account_type === User.ACCOUNT_TYPE_BOT)
        return null;
    if (authTabIndex < 0 || activeTab !== authTabIndex)
        return null;
    return <>{children}</>;
}

function DesktopNotificationPermissionField() {
    const { t } = useTranslation();
    const { values } = useFormikContext<UserFormValues>();
    const channels = values.notificationPreferences?.channels || [];
    const wantsDesktop = channels.includes('desktop');
    const [permission, setPermission] = useState<DesktopPermission>(() => getDesktopNotificationPermission());
    const [requesting, setRequesting] = useState(false);

    useEffect(() => {
        setPermission(getDesktopNotificationPermission());
    }, [wantsDesktop]);

    if (!wantsDesktop)
        return null;

    if (!isDesktopNotificationSupported()) {
        return (
            <p className="mt-2 text-sm text-amber-700">
                {t('users.notification_desktop_unsupported')}
            </p>
        );
    }

    const onRequestPermission = async () => {
        setRequesting(true);
        try {
            const next = await requestDesktopNotificationPermission();
            setPermission(next);
        } finally {
            setRequesting(false);
        }
    };

    if (permission === 'granted') {
        return (
            <p className="mt-2 text-sm text-green-700">
                {t('users.notification_desktop_granted')}
            </p>
        );
    }

    if (permission === 'denied') {
        return (
            <p className="mt-2 text-sm text-amber-700">
                {t('users.notification_desktop_denied')}
            </p>
        );
    }

    return (
        <div className="mt-2 flex flex-wrap items-center gap-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('users.notification_desktop_permission_needed')}
            </p>
            <Button
                type="button"
                color="primary"
                small
                loading={requesting}
                disabled={requesting}
                onClick={onRequestPermission}
                title={String(requesting
                    ? t('users.notification_desktop_enabling')
                    : t('users.notification_desktop_enable'))}
                icon="notification"
            />
        </div>
    );
}

function UserFormJwtExpiry() {
    const { t } = useTranslation();
    const { values } = useFormikContext<UserFormValues>();
    if (values.account_type !== User.ACCOUNT_TYPE_BOT)
        return null;
    const options = ['12h', '24h', '7d', '30d', '90d', '365d', '10y'].map((value) => ({
        value,
        label: value
    }));
    return (
        <div className="col-span-1">
            <LabelWithHelp help={t('users.help.jwt_expires') || ''}>
                {t('users.jwt_expires_in')}
            </LabelWithHelp>
            <div className="mt-2">
                <SelectField
                    name="jwt_expires_in"
                    placeholder={String(t('users.jwt_expires_in'))}
                    options={options}
                />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('users.jwt_expires_in_help')}</p>
        </div>
    );
}

function UserForm(props: UserFormProps) {
    const { isCreation, initialValues, onSubmit, isLoading } = props;

    const { t }                     = useTranslation();
    const dispatch                  = useAppDispatch();
    const { user }                  = useAppSelector((state: RootState) => state.auth);
    const { disabling }             = useAppSelector((state: RootState) => state.users);
    const [activeTab, setActiveTab] = useState(0);
    const [personalTokens, setPersonalTokens] = useState<AnyObj[]>([]);
    const [isLoadingTokens, setIsLoadingTokens] = useState(false);
    const [isCreatingToken, setIsCreatingToken] = useState(false);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [createdToken, setCreatedToken] = useState<string | null>(null);
    const [tokenToRevoke, setTokenToRevoke] = useState<AnyObj | null>(null);
    const [activePreset, setActivePreset] = useState<'cursor'|'claude'|'vscode'|'codex'|'curl'>('cursor');
    const [connectionStatus, setConnectionStatus] = useState<'idle'|'testing'|'ok'|'error'>('idle');
    const [connectionMessage, setConnectionMessage] = useState<string>('');
    const [tokenForm, setTokenForm] = useState({
        name: '',
        expires_at: '',
        scopes: ['mcp:read', 'mcp:write', 'mcp:triage'] as string[]
    });

    const isOwnProfile = user?._id === initialValues?._id;
    const hasPatTab = !isCreation && isOwnProfile;
    const hasCalendarTab = !isCreation && isOwnProfile;
    const canEditProjects = !isCreation && !!user?.can('project:members:edit');
    const isAdmin = user?.role === User.ROLE_ADMIN;

    const formatDatetime = (value?: string | null) => {
        if (!value)
            return '-';
        return new Date(value).toLocaleString();
    };

    const mcpHost = window.location.hostname;
    const mcpBaseUrl = `${window.location.protocol}//${mcpHost}${mcpHost === 'localhost' ? ':3002' : ''}`;
    const mcpUrl = `${mcpBaseUrl}/mcp`;
    const tokenValue = createdToken || "yoda_pat_xxxxxxxxxxxxxxxxx";
    const mcpPresets = useMemo(() => {
        const cursor = JSON.stringify({
            mcpServers: {
                "yoda-local": {
                    url: mcpUrl,
                    headers: {
                        Authorization: `Bearer ${tokenValue}`
                    }
                }
            }
        }, null, 2);

        const claude = JSON.stringify({
            mcpServers: {
                "yoda-local": {
                    url: mcpUrl,
                    headers: {
                        Authorization: `Bearer ${tokenValue}`
                    }
                }
            }
        }, null, 2);

        const vscode = JSON.stringify({
            servers: {
                "yoda-local": {
                    url: mcpUrl,
                    headers: {
                        Authorization: `Bearer ${tokenValue}`
                    }
                }
            }
        }, null, 2);

        const codex = [
            `codex mcp add yoda-local --url ${mcpUrl}`,
            "# Then configure Authorization header in your Codex MCP settings:",
            `# Authorization: Bearer ${tokenValue}`
        ].join('\n');

        const curl = `curl -H "Authorization: Bearer ${tokenValue}" ${mcpUrl}/health`;

        return {
            cursor,
            claude,
            vscode,
            codex,
            curl
        };
    }, [mcpUrl, tokenValue]);

    const loadPersonalTokens = useCallback(async () => {
        if (!hasPatTab)
            return;

        setIsLoadingTokens(true);
        setTokenError(null);
        try {
            const tokens = await Users.listMcpPersonalAccessTokens();
            setPersonalTokens(tokens);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('common.error');
            setTokenError(message);
        } finally {
            setIsLoadingTokens(false);
        }
    }, [hasPatTab, t]);

    useEffect(() => {
        if (!hasPatTab)
            return;

        const defaultExpiration = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        defaultExpiration.setSeconds(0, 0);
        const localDateString = new Date(defaultExpiration.getTime() - defaultExpiration.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);

        setTokenForm((previous) => ({
            ...previous,
            expires_at: previous.expires_at || localDateString
        }));

        loadPersonalTokens();
    }, [hasPatTab, loadPersonalTokens]);

    const toggleScope = (scope: string) => {
        setTokenForm((previous) => {
            const alreadySet = previous.scopes.includes(scope);
            return {
                ...previous,
                scopes: alreadySet
                    ? previous.scopes.filter((s) => s !== scope)
                    : [...previous.scopes, scope]
            };
        });
    };

    const createPersonalToken = async () => {
        if (!tokenForm.name.trim()) {
            setTokenError(t('users.pat_name_required'));
            return;
        }
        if (!tokenForm.expires_at) {
            setTokenError(t('users.pat_expires_required'));
            return;
        }
        if (!tokenForm.scopes.length) {
            setTokenError(t('users.pat_scope_required'));
            return;
        }

        setIsCreatingToken(true);
        setTokenError(null);
        setCreatedToken(null);
        try {
            const created = await Users.createMcpPersonalAccessToken({
                name: tokenForm.name.trim(),
                scopes: tokenForm.scopes,
                expires_at: new Date(tokenForm.expires_at).toISOString()
            });
            setCreatedToken(created.token);
            setTokenForm((previous) => ({ ...previous, name: '' }));
            await loadPersonalTokens();
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('common.error');
            setTokenError(message);
        } finally {
            setIsCreatingToken(false);
        }
    };

    const revokePersonalToken = async (tokenId: string) => {
        setTokenError(null);
        try {
            await Users.revokeMcpPersonalAccessToken(tokenId);
            await loadPersonalTokens();
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('common.error');
            setTokenError(message);
        }
    };

    const confirmRevokePersonalToken = async () => {
        if (!tokenToRevoke?._id)
            return;
        await revokePersonalToken(tokenToRevoke._id);
        setTokenToRevoke(null);
    };

    const copyCreatedToken = async () => {
        if (!createdToken)
            return;
        try {
            await navigator.clipboard.writeText(createdToken);
        } catch (_error) {
            setTokenError(t('users.pat_copy_failed'));
        }
    };

    const copyMcpConfig = async () => {
        try {
            await navigator.clipboard.writeText(mcpPresets[activePreset]);
        } catch (_error) {
            setTokenError(t('users.pat_copy_failed'));
        }
    };

    const testMcpConnection = async () => {
        setConnectionStatus('testing');
        setConnectionMessage('');
        try {
            const response = await fetch(`${mcpUrl}/health`);
            if (!response.ok) {
                setConnectionStatus('error');
                setConnectionMessage(String(t('users.pat_mcp_test_error_http', { code: response.status })));
                return;
            }
            setConnectionStatus('ok');
            setConnectionMessage(String(t('users.pat_mcp_test_ok')));
        } catch (_error) {
            setConnectionStatus('error');
            setConnectionMessage(String(t('users.pat_mcp_test_error_network')));
        }
    };

    const validate = (values: AnyObj) => {
        const errors: AnyObj = {};
        const isBot = values.account_type === User.ACCOUNT_TYPE_BOT;

        if (!values.email) {
            errors.email = 'Required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
            errors.email = t('users.email_is_invalid');
        }

        if (!isBot && values.phone !== undefined && values.phone !== null && String(values.phone).trim() === '')
            errors.phone = 'Required';

        if (!isBot && values.password?.length > 0 && values.password_confirmation?.length > 0) {
            if (values.password !== values.password_confirmation)
                errors.password_confirmation = t('users.password_mismatch');
        }

        const channels = values.notificationPreferences?.channels;
        if (!Array.isArray(channels) || channels.length === 0) {
            errors.notificationPreferences = {
                ...(errors.notificationPreferences || {}),
                channels: t('users.notification_channels_required'),
            };
        }

        return errors;
    }

    // Admins can always change any user's role (needed after JIT SSO
    // creates a minimal user with a default role). Other editors keep
    // the previous restriction: they cannot change another admin's role.
    const roles = User.roles().filter(role => user?.role === User.ROLE_ADMIN || role !== User.ROLE_ADMIN);
    const canEditRole = user?.role === User.ROLE_ADMIN || (
        !!user?.can('user:role:edit')
        && (user?._id === initialValues?._id || initialValues?.role !== User.ROLE_ADMIN)
    );

    const onAvatarChange = (imageList: ImageListType) => {
        if (imageList.length > 0) {
            const file: File | undefined = imageList[0].file;
            if (file)
                return dispatch(setUserAvatar(user._id, file));
        }
    };

    const removeAvatar = () => {
        return dispatch(unsetUserAvatar(user._id));
    }

    const formikInitialValues = useMemo(() => {
        const values: AnyObj = {
            ...initialValues,
            account_type: initialValues.account_type ?? User.ACCOUNT_TYPE_HUMAN,
            jwt_expires_in: (initialValues as AnyObj).jwt_expires_in || '30d',
            appearancePreferences: {
                theme: normalizeTheme((initialValues as AnyObj).appearancePreferences?.theme),
                fontFamily: normalizeFontFamily((initialValues as AnyObj).appearancePreferences?.fontFamily),
            },
            trackingPreferences: {
                defaultProjectId: (initialValues as AnyObj).trackingPreferences?.defaultProjectId || '',
                defaultBillingMode: (initialValues as AnyObj).trackingPreferences?.defaultBillingMode || '',
            },
            uiPreferences: {
                language: (initialValues as AnyObj).uiPreferences?.language || '',
                homePage: (initialValues as AnyObj).uiPreferences?.homePage || 'dashboard',
            },
        };
        if (canEditProjects)
            values.projects = Array.isArray((initialValues as AnyObj).projects)
                ? (initialValues as AnyObj).projects
                : [];
        return values;
    }, [initialValues, canEditProjects]);

    const canPreviewTheme = !isCreation && user?._id === initialValues?._id;

    return (
        <div className="ProjectForm bg-white dark:bg-gray-900 border-b dark:border-gray-700 p-4 sm:p-6">
            <main className="text-center md:text-left">
                <Formik
                    initialValues={formikInitialValues}
                    validate={validate}
                    enableReinitialize
                    onSubmit={(values) => {
                        const next: AnyObj = { ...values };
                        const defaultProjectId = next.trackingPreferences?.defaultProjectId;
                        next.trackingPreferences = {
                            defaultProjectId: defaultProjectId ? defaultProjectId : null,
                            defaultBillingMode: next.trackingPreferences?.defaultBillingMode || undefined,
                        };
                        if (!next.trackingPreferences.defaultBillingMode)
                            delete next.trackingPreferences.defaultBillingMode;
                        next.uiPreferences = {
                            language: next.uiPreferences?.language || undefined,
                            homePage: next.uiPreferences?.homePage || 'dashboard',
                        };
                        if (!next.uiPreferences.language)
                            delete next.uiPreferences.language;
                        next.notificationPreferences = {
                            ...(next.notificationPreferences || {}),
                            channels: next.notificationPreferences?.channels || ['email'],
                            language: next.notificationPreferences?.language || undefined,
                        };
                        if (!next.notificationPreferences.language)
                            delete next.notificationPreferences.language;
                        if (canPreviewTheme) {
                            const language = normalizeUiLanguage(next.uiPreferences.language);
                            if (language)
                                i18n.changeLanguage(language);
                        }
                        onSubmit(next);
                    }}
                >
                    <UserFormTabsBridge
                        isCreation={isCreation}
                        hasPatTab={hasPatTab}
                        hasCalendarTab={hasCalendarTab}
                        canEditProjects={canEditProjects}
                    >
                        {({ tabs, preferencesTabIndex, authTabIndex, projectsTabIndex, patTabIndex, calendarTabIndex }) => (
                    <>
                    <Form className="flex w-full flex-col md:flex-row md:items-start">
                <div id="default-sidebar" className="z-40 w-full shrink-0 md:w-64">
                    <div className="p-0 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded">
                        {!isCreation && (
                            <div className="flex justify-center">
                                <ImageUploader hidePreview onChange={onAvatarChange} className="!border-0">
                                    <div className="group relative">
                                        <UserAvatar noCache user={new User(initialValues)} size={24} noTooltip hideInitials />
                                        {initialValues.avatar && (
                                            <Button
                                                title=""
                                                icon="delete"
                                                onClick={removeAvatar}
                                                iconColor="red-600"
                                                className="hidden group-hover:block absolute -bottom-3 -right-5"
                                                color="none"
                                            />
                                        )}
                                    </div>
                                </ImageUploader>
                            </div>
                        )}
                        <UserFormTabSidebar
                            tabs={tabs}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            t={t}
                        />
                    </div>
                </div>
                <div className="min-w-0 flex-1 px-4 py-4">
                            <UserFormTabReset activeTab={activeTab} setActiveTab={setActiveTab} isCreation={isCreation} />
                            <div className="space-y-12">
                                <div className="flex flex-col sm:flex-row space-y-6 sm:space-y-0 space-x-0 sm:space-x-12">
                                    {activeTab === 0 && (
                                        <div className="flex-1 space-y-3">
                                            {!isCreation && (
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    {t('users.account_type')}:{' '}
                                                    <strong>{t(`users.account_type_${initialValues?.account_type || User.ACCOUNT_TYPE_HUMAN}`)}</strong>
                                                </p>
                                            )}
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                                <div className="col-span-1">
                                                    <label htmlFor="firstname" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('users.firstname')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="firstname"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            required
                                                        />
                                                        <ErrorMessage name="firstname" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                    </div>
                                                </div>
                                                <div className="col-span-1">
                                                    <label htmlFor="lastname" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('users.lastname')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="lastname"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            required
                                                        />
                                                        <ErrorMessage name="lastname" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                                <div className="col-span-1">
                                                    <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('users.email')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="email"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            required
                                                        />
                                                        <ErrorMessage name="email" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                    </div>
                                                </div>
                                                <div className="col-span-1">
                                                    <label htmlFor="phone" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('users.phone')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="phone"
                                                            placeholder="+41794562136"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                        <ErrorMessage name="phone" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                                {canEditRole && (
                                                    <div className="col-span-1">
                                                        <LabelWithHelp htmlFor="role" help={t('users.help.role') || ''}>
                                                            {t('users.role')}
                                                        </LabelWithHelp>
                                                        <div className="mt-2">
                                                            <SelectField
                                                                name="role"
                                                                placeholder={t('users.choose_a_role') || ''}
                                                                options={roles.map((role: string) => ({
                                                                    value: role,
                                                                    label: t(`users.role_${role}`)
                                                                }))}
                                                            />
                                                            <ErrorMessage name="role" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                        </div>
                                                    </div>
                                                )}
                                                {initialValues?.role !== User.ROLE_CLIENT && (
                                                    <div className="col-span-1">
                                                        <LabelWithHelp htmlFor="work_rate" help={t('users.help.work_rate') || ''}>
                                                            {t('users.work_rate')}
                                                        </LabelWithHelp>
                                                        <div className="mt-2">
                                                            <Field
                                                                type="number"
                                                                name="work_rate"
                                                                placeholder="100"
                                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                                required
                                                            />
                                                            <ErrorMessage name="work_rate" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {isCreation && (
                                                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                                    <div className="col-span-1">
                                                        <LabelWithHelp help={t('users.help.account_type') || ''}>
                                                            {t('users.account_type')}
                                                        </LabelWithHelp>
                                                        <div className="mt-2">
                                                            <SelectField
                                                                name="account_type"
                                                                placeholder={t('users.account_type') || ''}
                                                                options={[
                                                                    { value: User.ACCOUNT_TYPE_HUMAN, label: t('users.account_type_human') },
                                                                    { value: User.ACCOUNT_TYPE_BOT, label: t('users.account_type_bot') },
                                                                ]}
                                                            />
                                                        </div>
                                                    </div>
                                                    <UserFormJwtExpiry />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <PreferencesTabFields
                                        preferencesTabIndex={preferencesTabIndex}
                                        activeTab={activeTab}
                                        canPreviewTheme={!!canPreviewTheme}
                                    />

                                    <PasswordTabFields authTabIndex={authTabIndex} activeTab={activeTab}>
                                        <div className="flex-1 space-y-3">
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                                <div className="col-span-1">
                                                    <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('users.password')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="password"
                                                            name="password"
                                                            autoComplete="new-password"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                        <ErrorMessage name="password" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                    </div>
                                                </div>
                                                <div className="col-span-1">
                                                    <label htmlFor="password-confirmation" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('users.password_confirmation')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="password"
                                                            name="password_confirmation"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                        <ErrorMessage name="password_confirmation" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </PasswordTabFields>

                                    {projectsTabIndex >= 0 && activeTab === projectsTabIndex && (
                                        <div className="flex-1 space-y-3 text-left">
                                            <div>
                                                <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                    {t('users.projects')}
                                                </label>
                                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('users.projects_help')}</p>
                                                <div className="mt-2">
                                                    <ProjectSelectField
                                                        name="projects"
                                                        multiple
                                                        placeholder={t('users.select_projects') || ''}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <UserFormPatTab patTabIndex={patTabIndex} activeTab={activeTab}>
                                        <div className="flex-1 space-y-5 text-left">
                                            <div className="rounded border bg-gray-50 dark:bg-gray-900 p-4">
                                                <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('users.pat_create_title')}</h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{t('users.pat_create_desc')}</p>
                                                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                                    <div className="col-span-1">
                                                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                            {t('users.pat_name')}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={tokenForm.name}
                                                            onChange={(event) => setTokenForm((previous) => ({ ...previous, name: event.target.value }))}
                                                            className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                                            placeholder={t('users.pat_name_placeholder') || ''}
                                                        />
                                                    </div>
                                                    <div className="col-span-1">
                                                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                            {t('users.pat_expires_at')}
                                                        </label>
                                                        <input
                                                            type="datetime-local"
                                                            value={tokenForm.expires_at}
                                                            onChange={(event) => setTokenForm((previous) => ({ ...previous, expires_at: event.target.value }))}
                                                            className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-4">
                                                    <p className="mb-2 inline-flex items-center gap-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('users.pat_scopes')}
                                                        <FieldHelp content={t('users.help.pat_scopes') || ''} />
                                                    </p>
                                                    <div className="space-y-2">
                                                        {['mcp:read', 'mcp:write', 'mcp:triage'].map((scope) => (
                                                            <div key={scope} className="flex items-center justify-between rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
                                                                <span className="text-sm text-gray-700 dark:text-gray-200">{t(`users.pat_scope_${scope.replace(':', '_')}`)}</span>
                                                                <ToggleSwitch
                                                                    checked={tokenForm.scopes.includes(scope)}
                                                                    onChange={() => toggleScope(scope)}
                                                                    label=""
                                                                    className="inline"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="mt-4">
                                                    <Button
                                                        title={t('users.pat_create')}
                                                        color="primary"
                                                        icon="plus"
                                                        loading={isCreatingToken}
                                                        onClick={createPersonalToken}
                                                    />
                                                </div>
                                            </div>

                                            {createdToken && (
                                                <div className="rounded border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 p-4">
                                                    <p className="text-sm font-semibold text-yellow-800">{t('users.pat_created_once')}</p>
                                                    <p className="text-sm text-yellow-700 mb-2">{t('users.pat_created_copy_now')}</p>
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                        <code className="block flex-1 overflow-auto rounded bg-white dark:bg-gray-800 p-2 text-xs text-gray-900 dark:text-gray-100">{createdToken}</code>
                                                        <Button
                                                            title={t('users.pat_copy')}
                                                            color="secondary"
                                                            icon="copy"
                                                            onClick={copyCreatedToken}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {tokenError && (
                                                <div className="rounded border border-red-300 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700">
                                                    {tokenError}
                                                </div>
                                            )}

                                            <div className="rounded border bg-white dark:bg-gray-800">
                                                <div className="border-b px-4 py-3 flex justify-between items-center">
                                                    <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100">{t('users.pat_existing_tokens')}</h3>
                                                    <Button
                                                        title={t('users.pat_refresh')}
                                                        color="default"
                                                        icon="sync"
                                                        onClick={loadPersonalTokens}
                                                        loading={isLoadingTokens}
                                                    />
                                                </div>
                                                <div className="divide-y">
                                                    {!isLoadingTokens && personalTokens.length === 0 && (
                                                        <p className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{t('users.pat_empty')}</p>
                                                    )}
                                                    {personalTokens.map((token) => (
                                                        <div key={token._id} className="px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                    {token.name} <span className="text-gray-500 dark:text-gray-400">({token.token_prefix}...)</span>
                                                                </p>
                                                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                                                    {t('users.pat_scopes')}: {(token.scopes || []).join(', ')}
                                                                </p>
                                                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                                                    {t('users.pat_expires_at')}: {formatDatetime(token.expiresAt)}
                                                                </p>
                                                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                                                    {t('users.pat_last_used_at')}: {formatDatetime(token.lastUsedAt)}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                {token.revokedAt ? (
                                                                    <span className="inline-block rounded bg-gray-200 dark:bg-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200">
                                                                        {t('users.pat_revoked')}
                                                                    </span>
                                                                ) : (
                                                                    <Button
                                                                        title={t('users.pat_revoke')}
                                                                        color="danger"
                                                                        icon="delete"
                                                                        onClick={() => setTokenToRevoke(token)}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-4">
                                                <p className="text-sm font-semibold text-blue-800">{t('users.pat_mcp_config_title')}</p>
                                                <p className="text-sm text-blue-700 mb-2">{t('users.pat_mcp_config_desc')}</p>
                                                <div className="mb-3 flex flex-wrap gap-2">
                                                    {[
                                                        { id: 'cursor', label: t('users.pat_preset_cursor') },
                                                        { id: 'claude', label: t('users.pat_preset_claude') },
                                                        { id: 'vscode', label: t('users.pat_preset_vscode') },
                                                        { id: 'codex', label: t('users.pat_preset_codex') },
                                                        { id: 'curl', label: t('users.pat_preset_curl') }
                                                    ].map((preset) => (
                                                        <button
                                                            key={preset.id}
                                                            type="button"
                                                            onClick={() => setActivePreset(preset.id as 'cursor'|'claude'|'vscode'|'codex'|'curl')}
                                                            className={`rounded px-2 py-1 text-xs font-medium ${
                                                                activePreset === preset.id
                                                                    ? 'bg-blue-600 text-white'
                                                                    : 'bg-white dark:bg-gray-800 text-blue-700 border border-blue-300'
                                                            }`}
                                                        >
                                                            {preset.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                    <pre className="block flex-1 overflow-auto rounded bg-white dark:bg-gray-800 p-2 text-xs text-gray-900 dark:text-gray-100">{mcpPresets[activePreset]}</pre>
                                                    <Button
                                                        title={t('users.pat_copy_mcp_config')}
                                                        color="secondary"
                                                        icon="copy"
                                                        onClick={copyMcpConfig}
                                                    />
                                                </div>
                                                <div className="mt-3 rounded border border-blue-300 bg-white dark:bg-gray-800 p-3">
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('users.pat_mcp_test_title')}</p>
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                        <Button
                                                            title={t('users.pat_mcp_test_button')}
                                                            color="default"
                                                            icon="sync"
                                                            loading={connectionStatus === 'testing'}
                                                            onClick={testMcpConnection}
                                                        />
                                                        {connectionStatus !== 'idle' && (
                                                            <span className={`text-sm ${
                                                                connectionStatus === 'ok' ? 'text-green-700' : connectionStatus === 'error' ? 'text-red-700' : 'text-gray-700 dark:text-gray-200'
                                                            }`}>
                                                                {connectionStatus === 'testing' ? t('users.pat_mcp_test_pending') : connectionMessage}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </UserFormPatTab>

                                    {calendarTabIndex >= 0 && activeTab === calendarTabIndex && (
                                        <CalendarSubscriptionPanel isAdmin={!!isAdmin} />
                                    )}
                                </div>
                            </div>

                            <UserFormSaveBar
                                isCreation={isCreation}
                                activeTab={activeTab}
                                authTabIndex={authTabIndex}
                                patTabIndex={patTabIndex}
                                calendarTabIndex={calendarTabIndex}
                                isLoading={isLoading}
                            />
                </div>
                        </Form>
                    <Confirm
                        visible={!!tokenToRevoke}
                        iconType="warning"
                        title={t('users.pat_confirm_revoke', { name: tokenToRevoke?.name || '' }) || ''}
                        confirmText={t('users.pat_revoke') || ''}
                        cancelText={t('common.cancel') || ''}
                        onConfirm={confirmRevokePersonalToken}
                        onCancel={() => setTokenToRevoke(null)}
                    />
                    </>
                        )}
                    </UserFormTabsBridge>
                    </Formik>
                {!isCreation && initialValues?._id && (
                    <DangerZone
                        deleteTo={`/users/delete/${initialValues._id}`}
                        description={t('users.danger_zone_hint') as string}
                        archived={initialValues.disabled === true}
                        archiveLoading={disabling === 'pending'}
                        archiveTitle={t('users.deactivate') as string}
                        unarchiveTitle={t('users.activate') as string}
                        onArchive={() => dispatch(deactivateUser(initialValues._id))}
                        onUnarchive={() => dispatch(activateUser(initialValues._id))}
                    />
                )}
            </main>
        </div>
    );
}

export default UserForm;
